import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { userLogOut, userClearLogOut } from '../../../hooks/local/userReducer';
import logoWhite from '../../../assets/images/cloudVantage Logo White.png';
import {
  Database,
  Globe,
  Logs,
  ServerCrash,
  FileLock2,
  LayoutDashboard,
  Settings,
  CreditCard,
  LifeBuoy,
  LogOut,
  Search,
  ChevronRight,
  Edit2,
  BookOpen,
  User
} from 'lucide-react';

const SidebarSection = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="px-4 mb-2 text-[10px] font-bold uppercase tracking-widest text-white/40">
      {title}
    </h3>
    <div className="space-y-1">
      {children}
    </div>
  </div>
);

const SidebarItem = ({ item, isActive, isOpen, toggleDropdown, onClick }) => {
  const content = (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group cursor-pointer
        ${isActive 
          ? 'bg-primaryBlue text-white shadow-lg shadow-blue-500/20' 
          : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
      onClick={() => item.sublinks ? toggleDropdown(item.label) : onClick?.()}
    >
      <div className={`p-1.5 rounded-lg ${isActive ? 'bg-white/20' : 'bg-white/5 group-hover:bg-white/10'}`}>
        {item.iconComponent}
      </div>
      <span className="text-[13px] font-medium flex-1 truncate">{item.label}</span>
      {item.sublinks && (
        <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
      )}
    </div>
  );

  if (item.sublinks) {
    return (
      <div className="grid">
        {content}
        {isOpen && (
          <div className="mt-1 ml-4 pl-4 border-l border-white/10 space-y-1 py-1">
            {item.sublinks.map((sublink, index) => (
              <Link 
                key={index} 
                to={sublink.path}
                className={`block px-4 py-2 text-[12px] rounded-lg transition-colors
                  ${isActive && sublink.active ? 'text-white font-medium' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                {sublink.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link to={item.path} onClick={item.onClick}>
      {content}
    </Link>
  );
};

const SideBar = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [dropdownStates, setDropdownStates] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (setIsMobileMenuOpen) setIsMobileMenuOpen(false);
  }, [location.pathname, setIsMobileMenuOpen]);

  useEffect(() => {

  if (searchQuery.includes('@')) {

    setSearchQuery('');

  }

}, [searchQuery]);

  const toggleDropdown = (label) => {
    setDropdownStates((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const confirmLogout = () => {
    dispatch(userLogOut());
    dispatch(userClearLogOut());
    navigate('/');
  };

  const sections = [
    {
      title: "Main Menu",
      items: [
        { path: '/client-area', label: 'Dashboard', iconComponent: <LayoutDashboard className="w-4 h-4" /> },
        // { path: '/client-area/manage', label: 'Manage Project', iconComponent: <Edit2 className="w-4 h-4" /> },
        { path: '/manageServerSnapshots', label: 'Server Snapshots', iconComponent: <ServerCrash className="w-4 h-4" /> },
        {
          label: 'Volumes',
          iconComponent: <Database className="w-4 h-4" />,
          sublinks: [
            { path: '/client-area/volumes', label: 'Volumes' },
            { path: '/manageVolumeSnapshots', label: 'Volume Snapshots' },
            { path: '/manageVolumeBackups', label: 'Volume Backups' },
          ],
        },
      ]
    },
    {
      title: "Networks",
      items: [
        {
          label: 'Networks',
          iconComponent: <Globe className="w-4 h-4" />,
          sublinks: [
            { path: '/networks', label: 'Private Networks' },
            { path: '/securityGroups', label: 'Security Groups' },
            { path: '/routers', label: 'Routers' },
            { path: '/floating-ips', label: 'Floating IPs' },
            { path: '/manageLoadBalancers', label: 'Load Balancers' },
          ],
        },
      ]
    },
    {
      title: "Security",
      items: [
        { path: '/certificate-manager', label: 'Certificates', iconComponent: <FileLock2 className="w-4 h-4" /> },
      ]
    },
    // {
    //   title: "Billing",
    //   items: [
    //     { path: '/billing', label: 'Billing Center', iconComponent: <CreditCard className="w-4 h-4" /> },
    //   ]
    // },
    {
      title: "Support",
      items: [
        { path: '/help', label: 'Support Desk', iconComponent: <LifeBuoy className="w-4 h-4" /> },
        { path: '/event-logs', label: 'Event Logs', iconComponent: <Logs className="w-4 h-4" /> },
      ]
    },
    {
      title: "General",
      items: [
        { path: '/profile', label: 'My Profile', iconComponent: <User className="w-4 h-4" /> },
        { path: '/settings', label: 'Settings', iconComponent: <Settings className="w-4 h-4" /> },
      ]
    }

  ];

  const filteredSections = searchQuery 
    ? sections.map(section => {
        const filteredItems = section.items.map(item => {
          const itemMatch = item.label.toLowerCase().includes(searchQuery.toLowerCase());
          const matchingSublinks = item.sublinks 
            ? item.sublinks.filter(sub => sub.label.toLowerCase().includes(searchQuery.toLowerCase()))
            : [];
            
          if (itemMatch || matchingSublinks.length > 0) {
            return {
              ...item,
              sublinks: itemMatch && item.sublinks ? item.sublinks : (matchingSublinks.length > 0 ? matchingSublinks : undefined)
            };
          }
          return null;
        }).filter(Boolean);
        
        return {
          ...section,
          items: filteredItems
        };
      }).filter(section => section.items.length > 0)
    : sections;

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setIsMobileMenuOpen?.(false)}
        />
      )}

      <aside className={`w-64 bg-[#0a1b33] border-r border-white/5 flex flex-col h-screen fixed lg:sticky top-0 left-0 overflow-hidden shadow-2xl z-50 shrink-0 transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
      {/* Header / Logo */}
      <div className="p-6">
        <Link to="/client-area" className="block">
          <img src={logoWhite} alt="CloudVantage" className="h-6 object-contain" />
        </Link>
        <span className="mt-2 inline-block text-[10px] font-bold uppercase tracking-widest text-primaryBlue/80 bg-primaryBlue/10 border border-primaryBlue/20 px-2 py-0.5 rounded-full">
          Student Portal
        </span>
      </div>

      {/* Search Bar Area */}
      <div className="px-4 mb-6">
        <div className="relative group">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 group-focus-within:text-primaryBlue transition-colors" />
          <input
            type="search"
            placeholder="Search..."
            autoComplete="new-password"
            name="cloudvantage-sidebar-search"
            id="cloudvantage-sidebar-search"
            inputMode="search"
            className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-10 pr-10 text-[13px] text-white placeholder:text-white/20 focus:outline-none focus:border-primaryBlue/50 focus:bg-white/10 transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded border border-white/10 bg-white/5 text-[9px] font-bold text-white/30">
            ⌘ K
          </div>
        </div>
      </div>

      {/* Navigation Areas */}
      <div className="flex-1 overflow-y-auto px-2 custom-scrollbar pb-6">
        {filteredSections.length > 0 ? filteredSections.map((section, idx) => (
          <SidebarSection key={idx} title={section.title}>
            {section.items.map((item, itemIdx) => {
              const isActive = item.path === location.pathname || 
                item.sublinks?.some(sub => sub.path === location.pathname);
              
              return (
                <SidebarItem 
                  key={itemIdx}
                  item={item}
                  isActive={isActive}
                  isOpen={searchQuery ? true : dropdownStates[item.label]}
                  toggleDropdown={toggleDropdown}
                />
              );
            })}
          </SidebarSection>
        )) : (
          <div className="text-center text-white/40 text-[11px] py-10 px-4">
            No resources found for "{searchQuery}"
          </div>
        )}

        
        {/* Logout Item */}
        <SidebarItem 
          item={{ label: 'Logout', iconComponent: <LogOut className="w-4 h-4" /> }}
          onClick={() => setShowLogoutModal(true)}
          isActive={false}
        />
      </div>

      {/* Student Resources Card */}
      <div className="p-4 border-t border-white/5">
        <div className="bg-gradient-to-br from-[#0e2a4f] to-[#123866] border border-white/10 rounded-2xl p-4 relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-2 opacity-10 transform translate-x-1 -translate-y-1 group-hover:translate-x-0 group-hover:translate-y-0 transition-transform">
            <BookOpen className="w-12 h-12 text-white" />
          </div>
          <p className="text-white font-bold text-[13px] mb-1.5 relative z-10 flex items-center gap-2">
            <span className="bg-primaryBlue/80 p-1.5 rounded-lg"><BookOpen className="w-3.5 h-3.5 text-white" /></span>
            Learning Space
          </p>
          <p className="text-white/70 text-[10px] mb-3 relative z-10 leading-relaxed">
            Access tutorials, guides, and documentation to master cloud deployments.
          </p>
          <Link to="/client-area" className="block text-center w-full bg-white/10 hover:bg-primaryBlue text-white py-2 rounded-xl text-[11px] font-bold transition-all relative z-10">
            View Resources
          </Link>
        </div>
      </div>

    </aside>

    {/* Modern Logout Confirmation Modal — outside aside so it centers over the full app */}
    {showLogoutModal && (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowLogoutModal(false)}
        />
        <div className="relative bg-white dark:bg-[#141D26] rounded-2xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center gap-5">
          {/* Icon */}
          <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center">
            <LogOut className="w-7 h-7 text-red-500" />
          </div>

          {/* Text */}
          <div className="text-center">
            <h3 className="text-[18px] font-bold text-gray-900 dark:text-white mb-2">Sign out?</h3>
            <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
              Are you sure you want to sign out? You will need to sign in again to access your cloud resources.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 w-full">
            <button
              onClick={() => setShowLogoutModal(false)}
              className="flex-1 py-2.5 rounded-xl text-[14px] font-medium bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmLogout}
              className="flex-1 py-2.5 rounded-xl text-[14px] font-medium bg-red-500 text-white hover:bg-red-600 transition-all hover:shadow-lg hover:shadow-red-500/25"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    )}

    </>
  );
};

export default SideBar;
